import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { LoadingSpinner } from '../../common';
import { usePermission } from '../../../hooks/usePermission';
import { EdiService, formatCents, formatEdiDate } from '../../../services/billing/edi.service';
import type { EdiWorkActionRequest, EdiWorkItem, EdiWorkItemDetail, WorkAction } from '../../../dtos/billing/edi.dto';
import { BILLING_WORK, ErrorNote, SimulatedBadge, WORK_KIND_LABEL, WORK_STATE_LABEL, errorText, useCurrentUserId } from './ediUi';
import { parseFieldValue } from './fieldValue';

interface ExceptionDetailProps {
  workItemId: string;
  onBack: () => void;
  onChanged?: (item: EdiWorkItem) => void;
}

/** Billing fields a person may set (contract §4 MUTABLE_FIELDS). */
export const SETTABLE_FIELDS = ['payerRoute', 'modifiers', 'placeOfService', 'taxonomy', 'controlNumber', 'units', 'renderingNpiMode', 'frequencyCode', 'originalReference'] as const;

const TERMINAL = new Set(['RESOLVED', 'WRITTEN_OFF']);
const inputClass = 'w-full rounded-md border border-slate-300 px-2 py-1.5 text-base sm:text-sm';

type Form = 'note' | 'reply' | 'set_field' | 'snooze' | 'write_off' | null;

/** DEN-02 item detail: the exact reason, codes, amount, deadline, suggested action and the actions. */
const ExceptionDetail: React.FC<ExceptionDetailProps> = ({ workItemId, onBack, onChanged }) => {
  const canWork = usePermission(BILLING_WORK);
  const userId = useCurrentUserId();
  const [detail, setDetail] = useState<EdiWorkItemDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<Form>(null);
  const [text, setText] = useState('');
  const [field, setField] = useState<string>(SETTABLE_FIELDS[0]);
  const [value, setValue] = useState('');
  const [until, setUntil] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDetail(await EdiService.getWorkItem(workItemId));
    } catch (err) {
      setError(errorText(err, 'Failed to load the exception'));
    }
  }, [workItemId]);

  useEffect(() => { void load(); }, [load]);

  if (!detail) return error ? <div className="p-3"><ErrorNote message={error} /></div> : <LoadingSpinner label="Loading exception" />;
  const { item, claim } = detail;
  const open = !TERMINAL.has(item.state);

  const submit = async (request: EdiWorkActionRequest) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await EdiService.actOnWorkItem(item.workItemId, request);
      setDetail({ item: updated, claim });
      onChanged?.(updated);
      setForm(null);
      setText('');
      setValue('');
      setUntil('');
      setFieldError(null);
    } catch (err) {
      setError(errorText(err, 'The action failed'));
    } finally {
      setBusy(false);
    }
  };

  const simple = (action: WorkAction, label: string, primary = false) => (
    <button
      type="button"
      disabled={busy}
      onClick={() => submit({ action })}
      className={primary ? 'rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white disabled:opacity-50' : 'rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 disabled:opacity-50'}
    >
      {label}
    </button>
  );
  const opener = (f: Exclude<Form, null>, label: string) => (
    <button type="button" disabled={busy} onClick={() => setForm(form === f ? null : f)} aria-expanded={form === f} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 disabled:opacity-50">
      {label}
    </button>
  );

  const codes = [item.reason.code, item.reason.carc && `CARC ${item.reason.carc}`, item.reason.rarc?.length ? `RARC ${item.reason.rarc.join(', ')}` : '', item.reason.evv && `EVV ${item.reason.evv}`].filter(Boolean);

  return (
    <div className="space-y-3 p-3 sm:p-4" aria-label="Exception detail">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft size={14} /> All exceptions
      </button>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold text-slate-800">{WORK_KIND_LABEL[item.kind] || item.kind}</h2>
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700" data-testid="work-state">{WORK_STATE_LABEL[item.state] || item.state}</span>
        {item.simulated && <SimulatedBadge />}
      </div>
      <p className="break-words rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-950" data-testid="work-reason">{item.reason.text || 'No reason text'}</p>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
        {codes.length > 0 && <div><dt className="inline text-slate-500">Codes: </dt><dd className="inline">{codes.join(' · ')}</dd></div>}
        {item.patientName && <div><dt className="inline text-slate-500">Patient: </dt><dd className="inline">{item.patientName}</dd></div>}
        {item.payerName && <div><dt className="inline text-slate-500">Payer: </dt><dd className="inline">{item.payerName}</dd></div>}
        {item.amountCents !== null && <div><dt className="inline text-slate-500">Amount: </dt><dd className="inline">{formatCents(item.amountCents)}</dd></div>}
        {item.deadline && <div><dt className="inline text-slate-500">Deadline: </dt><dd className="inline font-medium text-red-700">{formatEdiDate(item.deadline)}</dd></div>}
        {claim && <div><dt className="inline text-slate-500">Claim status: </dt><dd className="inline">{claim.status}{claim.submission?.controlNumber ? ` · control # ${claim.submission.controlNumber}` : ''}</dd></div>}
        {item.assigneeUserId && <div><dt className="inline text-slate-500">Assigned: </dt><dd className="inline">{item.assigneeUserId === userId ? 'You' : item.assigneeUserId}</dd></div>}
        {item.snoozedUntil && <div><dt className="inline text-slate-500">Snoozed until: </dt><dd className="inline">{formatEdiDate(item.snoozedUntil)}</dd></div>}
      </dl>
      {item.suggestedAction && (
        <p className="text-sm text-slate-700" data-testid="suggested-action">
          Suggested action: <span className="font-medium">{item.suggestedAction.type.replace(/_/g, ' ').toLowerCase()}</span>
          {Object.keys(item.suggestedAction.params).length > 0 && ` (${Object.entries(item.suggestedAction.params).map(([k, v]) => `${k}: ${String(v)}`).join(', ')})`}
        </p>
      )}

      <ErrorNote message={error} />

      {canWork && open && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2" aria-label="Actions">
            {item.state === 'OPEN' && simple('start', 'Start', true)}
            {simple('resolve', 'Resolve')}
            {claim && simple('resubmit', 'Resubmit')}
            {claim && item.kind === 'DENIAL' && simple('correct', 'Corrected claim')}
            {opener('set_field', 'Set field')}
            {item.kind === 'INFO_REQUEST' && opener('reply', 'Reply')}
            {opener('note', 'Add note')}
            {userId && item.assigneeUserId !== userId && (
              <button type="button" disabled={busy} onClick={() => submit({ action: 'assign', params: { assigneeUserId: userId } })} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 disabled:opacity-50">
                Assign to me
              </button>
            )}
            {opener('snooze', 'Snooze')}
            {opener('write_off', 'Write off')}
          </div>

          {(form === 'note' || form === 'reply' || form === 'write_off') && (
            <form
              className="space-y-2"
              onSubmit={(e) => { e.preventDefault(); if (text.trim()) void submit({ action: form, note: text }); }}
            >
              <label className="block text-sm text-slate-700">
                {form === 'reply' ? 'Reply' : form === 'write_off' ? 'Write-off reason' : 'Note'}
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} className={inputClass} />
              </label>
              <button type="submit" disabled={busy || !text.trim()} className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white disabled:opacity-50">
                {form === 'reply' ? 'Send reply' : form === 'write_off' ? 'Write off' : 'Save note'}
              </button>
            </form>
          )}
          {form === 'set_field' && (
            <form
              className="grid grid-cols-1 gap-2 sm:grid-cols-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (!value.trim()) return;
                const parsed = parseFieldValue(field, value);
                if ('error' in parsed) { setFieldError(parsed.error); return; }
                setFieldError(null);
                void submit({ action: 'set_field', params: { field, value: parsed.value }, note: text });
              }}
            >
              <label className="block text-sm text-slate-700">Field
                <select value={field} onChange={(e) => { setField(e.target.value); setFieldError(null); }} className={inputClass}>
                  {SETTABLE_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </label>
              <label className="block text-sm text-slate-700">Value
                <input value={value} onChange={(e) => { setValue(e.target.value); setFieldError(null); }} aria-invalid={fieldError ? true : undefined} aria-describedby={fieldError ? 'set-field-error' : undefined} className={inputClass} />
                {fieldError && <span id="set-field-error" role="alert" className="mt-1 block text-xs text-red-700">{fieldError}</span>}
              </label>
              <div className="flex items-end">
                <button type="submit" disabled={busy || !value.trim()} className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white disabled:opacity-50">Apply</button>
              </div>
            </form>
          )}
          {form === 'snooze' && (
            <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (until) void submit({ action: 'snooze', params: { until } }); }}>
              <label className="block text-sm text-slate-700">Snooze until
                <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className={inputClass} />
              </label>
              <button type="submit" disabled={busy || !until} className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white disabled:opacity-50">Snooze</button>
            </form>
          )}
        </div>
      )}

      {item.actions.length > 0 && (
        <section aria-label="Action history">
          <h3 className="mb-1 text-xs font-semibold uppercase text-slate-500">History</h3>
          <ul className="space-y-1 text-sm">
            {item.actions.map((a, i) => (
              <li key={`${a.at}-${i}`} className="break-words text-slate-700">
                {formatEdiDate(a.at)} · {a.action}{a.result ? ` · ${a.result}` : ''}{a.note ? ` — ${a.note}` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default ExceptionDetail;
